import {
    FormStep,
    InputType,
    RegistrationRequest,
    UserAttributeDefinition,
    UserInput,
    UserLoginPrincipal,
    UserPrimaryField
} from "../http/protocol";
import {UseFormRegister, UseFormSetValue, UseFormWatch} from "react-hook-form/dist/types/form";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {
    EMAIL_VALIDATION_OPTIONS,
    getRepeatedInputName,
    PASSWORD_VALIDATION_OPTIONS,
    toRegisterOptions,
    toSelectOptions
} from "./user-utils";
import {UserDetailsTextInput} from "./inputs/UserDetailsTextInput";
import {UserDetailsPhoneInput} from "./inputs/UserDetailsPhoneInput";
import {UserDetailsCheckbox} from "./inputs/UserDetailsCheckbox";
import {BrandLink} from "../ui/buttons/buttons";
import {t} from "../i18n";
import {UserDetailsPasswordInput} from "./inputs/UserDetailsPasswordInput";
import {PHONE_REGEX} from "../user/ui/form";
import {UserDetailsCountrySelect} from "./inputs/UserDetailsCountrySelect";
import {UserDetailsDateInput} from "./inputs/UserDetailsDateInput";
import {UserDetailsSelectInput} from "./inputs/UserDetailsSelectInput";
import * as React from "react";
import {useEffect, useRef} from "react";
import {FormProvider, UnpackNestedValue, useForm} from "react-hook-form";
import {StepIndicator} from "./StepIndicator";
import {PrincipalSelector} from "./PrincipalSelector";
import {SubmitOrNextButton} from "./SubmitOrNextButton";
import { UserDetailsPromoCodeInput } from "./inputs/UserDetailsPromoCodeInput";
import { auditEnabledByFlag } from "..";
import MetamapButton from "../temp/MetamapButton";
import { useSelector } from "react-redux";
import { AppState } from "../RootReducer";


export const createInput = (currentLoginPrincipal: UserLoginPrincipal,
                     input: UserInput,
                     register: UseFormRegister<any>,
                     errors: FieldErrors<any>,
                     watch: UseFormWatch<any>,
                     setValue: UseFormSetValue<any>,
                     attribute?: UserAttributeDefinition,
                     handleFocus?: any,
                     repeat?: boolean) => {
    let key = attribute?.attributeKey;
    let name: string | undefined = attribute ? "attributes." + key : input.field;

    if (!name) {
        return <></>
    }
    let originalName = undefined;
    if (repeat === true) {
        originalName = name;
        name = getRepeatedInputName(name);
    }
    switch (input.type) {
        case InputType.LOGIN_PRINCIPAL:
            if (currentLoginPrincipal == UserLoginPrincipal.EMAIL) {
                return <UserDetailsTextInput handleFocus={handleFocus} input={input} name={UserPrimaryField.email} register={register}
                                             errors={errors} options={EMAIL_VALIDATION_OPTIONS}/>;
            } else {
                return <UserDetailsPhoneInput handleFocus={handleFocus} input={input} setValue={setValue} name={UserPrimaryField.phoneNumber}
                                              register={register} errors={errors} options={{required: true}}/>;
            }
        case InputType.ACCEPT_TERMS:
            return <UserDetailsCheckbox input={input}
                                        name={name}
                                        register={register}
                                        errors={errors}
                                        options={{required: true}}
                                        link={
                                            <BrandLink target={"_blank"} className={"form-link"} to={"/info/terms"}>
                                                {t("terms-link")}
                                            </BrandLink>
                                        }
            />;
        case InputType.CHECKBOX:
            return <UserDetailsCheckbox input={input}
                                        name={name}
                                        register={register}
                                        errors={errors}
            />;
        case InputType.CHECKBOX_CHECKED:
            return <UserDetailsCheckbox input={input}
                                        name={name}
                                        register={register}
                                        errors={errors}
                                        defaultChecked={true}
                                        setValue={setValue}
            />;
        case InputType.AUTO_ACCEPT_TERMS:
            return <div className={"auto-accept-terms"}>{t("forms.acceptTerms")}</div>
        case InputType.EMAIL:
            return <UserDetailsTextInput handleFocus={handleFocus}  input={input} name={name} register={register} errors={errors}
                                         options={EMAIL_VALIDATION_OPTIONS}/>;
        case InputType.PASSWORD:
            return <UserDetailsPasswordInput handleFocus={handleFocus}  input={input} name={name} register={register} errors={errors} watch={watch}
                                             options={PASSWORD_VALIDATION_OPTIONS} repeat={originalName}/>;
        case InputType.PROMO_CODE:
            return <UserDetailsPromoCodeInput handleFocus={handleFocus} input={input} name={name} register={register} errors={errors} />;
        case InputType.PHONE:
            return <UserDetailsPhoneInput handleFocus={handleFocus}  input={input} setValue={setValue} name={name} register={register}
                                          errors={errors} options={{required: input.required, pattern: PHONE_REGEX}}/>;
        case InputType.COUNTRY:
            return <UserDetailsCountrySelect input={input} name={name} register={register} errors={errors}
                                             options={toRegisterOptions(input, attribute)}/>;
        case InputType.DATE_OF_BIRTH:
            return <UserDetailsDateInput input={input} setValue={setValue} name={name} register={register}
                                         errors={errors} options={toRegisterOptions(input, attribute)}
                                         minAge={input.minAge ?? 18}/>;
        default:
            if (attribute?.options && attribute.options.trim().length > 0) {
                return <UserDetailsSelectInput input={input} name={name} register={register} errors={errors} setValue={setValue}
                                               options={toSelectOptions(input, attribute)}
                                               attributeOptions={attribute.options}/>;
            } else {
                return <UserDetailsTextInput handleFocus={handleFocus} input={input} name={name} register={register} errors={errors}
                                             options={toRegisterOptions(input, attribute)}/>;

            }
    }

}

export interface SubmissionError {
    message?: string,
    field?: string
}
type HTMLInputEvent = React.ChangeEvent<HTMLInputElement>;
export interface FormStepResult {
    valid?: boolean;
    data: UnpackNestedValue<any>;
}

export const UserDetailsFormStep = (
    props: {
        step: FormStep,
        stepState?: RegistrationRequest
        loginPrincipals: UserLoginPrincipal[],
        principal: UserLoginPrincipal;
        setPrincipal: (l: UserLoginPrincipal) => void;
        inputFilter: (i: UserInput) => boolean;
        stepIndex: number,
        nrOfSteps: number,
        next: (result: FormStepResult, goToIndex?: number) => Promise<any> | void,
        submitError: SubmissionError,
        submitting?: boolean;
        getAttribute: (key?: string) => UserAttributeDefinition | undefined,
        allValid: boolean
    }) => {

    let methods = useForm({
        defaultValues: props.stepState,
        reValidateMode: 'onChange',
        mode: 'onTouched'
    });
    const {register, handleSubmit, formState: {errors, isValid}, watch, control, setValue, setFocus, setError} = methods;
    const [inputEvent, setInputEvent] = React.useState<string>("");
    const settingsTitle = useSelector((app: AppState) => app.settings.settings?.title)
    const handleFocus = (event: HTMLInputEvent) => {
        setInputEvent(event.type);
    }

    const index = props.stepIndex;
    const formRef = useRef<HTMLFormElement>(null);

    const onSubmit = (data: UnpackNestedValue<any>, event: any) => {
        //detail is the step index to go to
        props.next({valid: isValid, data: data}, event?.nativeEvent?.detail);
    };
    const onError = (data: UnpackNestedValue<any>, event: any) => {
        props.next({valid: isValid, data: data}, event?.nativeEvent?.detail);
    }
    const setPrincipal = (p: UserLoginPrincipal) => {
        if(p == UserLoginPrincipal.EMAIL) {
            setValue("phoneNumber", "");
        } else {
            setValue("email", "");
        }
        props.setPrincipal(p);
    }
    useEffect(() => {
        if (props.submitError.field) {
            setError(props.submitError.field as any, {message: props.submitError.message});
            setFocus(props.submitError.field as any);
        }
    }, [props.submitError.message, props.submitError.field])


    const selectStep = (step: number) => {
        //need to store the state when clicking on step number
        let event = new CustomEvent("submit", {detail: step, bubbles: true, cancelable: true});
        formRef.current?.dispatchEvent(event);
    }
    let submitCallback = handleSubmit(onSubmit, onError);
    return (
        <FormProvider {...methods} >
            {props.nrOfSteps > 1 && (
                <StepIndicator
                    selectStep={(step) => selectStep(step)}
                    nrOfSteps={props.nrOfSteps ?? 0} currentStep={props.stepIndex}/>
            )}
            <form ref={formRef} onSubmit={submitCallback} className={`form register-form ${inputEvent == "focus" ? "register-form-focus" : "" }`}>
                <div className={"step-container " + "step-" + (index + 1)} key={index}>
                     {props.step?.groups?.map((g, index) => (
                        <div className="user-input-group" key={"group-" + index}>
                            {g.translationKey && <h4 className={"user-input-group-title"}>{t(g.translationKey)}</h4>}
                            {
                                g.inputs?.filter(props.inputFilter).map((input, index) => {
                                    return (
                                        <div className={"form-group-container"} key={index}>
                                            {props.loginPrincipals.length > 1 && input.type == InputType.LOGIN_PRINCIPAL && (
                                                <PrincipalSelector principals={props.loginPrincipals}
                                                                   principal={props.principal}
                                                                   setPrincipal={setPrincipal}/>
                                            )}
                                            {createInput(props.principal, input, register, errors, watch, setValue, props.getAttribute(input.userAttributeKey), handleFocus)}
                                            {input.repeatInput && (
                                                createInput(props.principal, input, register, errors, watch, setValue, props.getAttribute(input.userAttributeKey), true)
                                            )}
                                        </div>
                                    )
                                })
                            }
                        </div>
                    ))
                    }
                    {settingsTitle?.includes("#") ? <MetamapButton values={watch()} isValid={!isValid || !props.allValid} className={`btn btn-cta full-width ${inputEvent == "focus" ? "btn-focus" : ""}`} type="button" /> :
                     <SubmitOrNextButton
                        className={inputEvent == "focus" ? "btn-focus" : ""}
                        nrOfSteps={props.nrOfSteps}
                        allValid={props.allValid}
                        submitError={props.submitError}
                        submitting={props.submitting}
                        isValid={isValid}
                        stepIndex={props.stepIndex} />
                        
                    }
                </div>
            </form>
        </FormProvider>


    )
}
